import React, { useState, useEffect, useRef } from 'react';
import './LED.css';

const LED = ({ transparentBackground, color = "#FF3B30", value, title, draggable, pin, dims }) => {
  const [isActive, setIsActive] = useState(parseFloat(value) !== 0);
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 300, height: 300 });
  const [cursorState, setCursorState] = useState('idle');

  const fontSizeMultiplier = typeof window !== 'undefined'
    ? parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--font-size-multiplier') || 1)
    : 1;

  useEffect(() => {
    setIsActive(parseFloat(value) !== 0);
  }, [value]);

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const calculateSizes = () => {
    const minDimension = Math.min(containerSize.width, containerSize.height);

    return {
      ledSize: Math.max(minDimension * 0.25, 40 * fontSizeMultiplier),
      glowSize: Math.max(minDimension * 0.4, 100 * fontSizeMultiplier),
      titleFontSize: Math.max(minDimension * 0.08, 16 * fontSizeMultiplier),
      statusFontSize: Math.max(minDimension * 0.05, 12 * fontSizeMultiplier),
      containerPadding: Math.max(minDimension * 0.03, 12 * fontSizeMultiplier),
      gapSize: Math.max(minDimension * 0.02, 8 * fontSizeMultiplier)
    };
  };

  const sizes = calculateSizes();

  useEffect(() => {
    if (!containerRef.current) return;

    const el = containerRef.current;

    el.style.setProperty('--led-font-mult', String(fontSizeMultiplier));
    el.style.setProperty('--led-color', String(color));

    el.style.setProperty('--led-size', `${sizes.ledSize}px`);
    el.style.setProperty('--led-glow-size', `${sizes.glowSize}px`);
    el.style.setProperty('--led-title-font-size', `${sizes.titleFontSize}px`);
    el.style.setProperty('--led-status-font-size', `${sizes.statusFontSize}px`);
    el.style.setProperty('--led-container-padding', `${sizes.containerPadding}px`);
    el.style.setProperty('--led-gap-size', `${sizes.gapSize}px`);
    el.style.setProperty('--led-border-width', `${Math.max(2, sizes.ledSize * 0.03)}px`);
  }, [fontSizeMultiplier, color, sizes.ledSize, sizes.glowSize, sizes.titleFontSize, sizes.statusFontSize, sizes.containerPadding, sizes.gapSize]);

  const cursorClass = draggable
    ? (cursorState === 'grabbing' ? 'led-widget--grabbing' : 'led-widget--grab')
    : 'led-widget--auto';

  return (
    <div
      ref={containerRef}
      className={[
        "widget-container",
        "led-widget",
        cursorClass,
        transparentBackground ? "led-widget--transparent" : "",
        isActive ? "led-widget--active" : "led-widget--inactive",
      ].filter(Boolean).join(" ")}
      onMouseDown={() => draggable && setCursorState('grabbing')}
      onMouseUp={() => draggable && setCursorState('grab')}
      onMouseLeave={() => draggable && setCursorState('grab')}
      draggable={draggable}
      onDragStart={(e) => {
        const id = e.target.closest('.widget')?.id;
        if (!id) return;
        const rect = e.target.getBoundingClientRect();
        const offsetX = e.clientX - rect.x;
        const offsetY = e.clientY - rect.y;
        let dragData = `led;${id};${offsetX};${offsetY};`;
        dragData += id.startsWith('base') ? 'add' : 'move';
        e.dataTransfer.setData('text/plain', dragData);
      }}
    >
      <div className="led-widget__title">
        {title}
      </div>

      <div className="led-widget__content">
        <div className="led-widget__inner">
          {isActive ? <div className="led-widget__glow" /> : null}

          <div className="led-widget__stack">
            <div className="led led-widget__ledCircle">
              <div className="led-widget__highlight led-widget__highlight--top" />
              <div className="led-widget__highlight led-widget__highlight--center" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LED;
